import { Button } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { SEO } from '../../components/common/SEO';
import { MarketingLayout } from './MarketingLayout';

/**
 * /secondary-schools - targets "school management system for secondary schools in
 * Zimbabwe". Every capability listed here exists in the product; keep it that way.
 */
export const SecondarySchoolsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <MarketingLayout current="/secondary-schools">
      <SEO
        title="School Management System for Secondary Schools in Zimbabwe | Rongeka Africa"
        description="Run a Zimbabwean secondary school from Form 1 to Upper Sixth: classes and streams, subject teachers, boarders and day scholars, exams, ZIMSEC-style report cards and fees."
        url="https://rongeka.africa/secondary-schools"
        keywords="school management system for secondary schools Zimbabwe, high school management software Zimbabwe, O Level A Level school system, secondary school fees software"
      />

      <header className="mkt-hero">
        <div className="landing-section-label">SECONDARY SCHOOLS</div>
        <h1 className="mkt-hero-title">School Management for Zimbabwean Secondary Schools</h1>
        <p className="mkt-hero-lead">
          A secondary school has more moving parts than most: streams in every form,
          subject teachers instead of class teachers, boarders and day scholars on
          different fees, and O Level and A Level results to report. Rongeka Africa
          handles all of it in one system.
        </p>
        <div className="mkt-hero-actions">
          <Button type="primary" size="large" onClick={() => navigate('/get-started')}>
            Start Your Free Term
          </Button>
          <Button size="large" onClick={() => navigate('/pricing')}>
            See Pricing
          </Button>
        </div>
      </header>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Forms, streams and subject teachers</h2>
          <p className="mkt-section-body">
            Set up your levels and the classes in each one, so every pupil sits in a named
            class such as Form 3 - East. The class register is kept per academic year, so
            last year's classes stay intact when pupils move up. Subjects can be tagged to
            a curriculum - ZIMSEC O Level, ZIMSEC A Level, Cambridge, or your own - and each
            subject is assigned to the teachers who take it, so a teacher sees only their
            own classes and pupils.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />Levels and classes for every form, shown as Form 2 - Blue</li>
            <li><CheckCircleOutlined />Subject enrolment per pupil and subject teacher assignment</li>
            <li><CheckCircleOutlined />Year-end promotions to the next form, with leavers kept on record</li>
            <li><CheckCircleOutlined />Attendance registers teachers can mark from WhatsApp</li>
          </ul>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Boarders, day scholars and fees</h2>
          <p className="mkt-section-body">
            Each pupil is recorded as a day scholar or a boarder, and charge items can apply
            to all pupils, to day scholars only, or to boarders only - so boarding fees land
            on the right accounts without manual adjustments. Fees are set per level and
            carry forward into later terms, arrears are tracked separately from the current
            term, and parents see their balance on WhatsApp.
          </p>
          <p className="mkt-section-body">
            Read more about <RouterLink to="/features/fees">school fees management</RouterLink> and{' '}
            <RouterLink to="/features/ecocash-payments">EcoCash fee payments</RouterLink>.
          </p>
        </div>
      </section>

      <section className="mkt-section">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">Exams, results and report cards</h2>
          <p className="mkt-section-body">
            Weekly tests, mid-term exams, end-of-term and end-of-year exams are each kept
            as their own sitting, with every subject paper inside it. Teachers enter marks
            for their own subjects; Rongeka applies your grading scale, works out class
            positions, and writes the subject and overall remarks from each pupil's own
            results, so teachers are not typing comments for hundreds of report cards.
          </p>
          <ul className="mkt-check-list">
            <li><CheckCircleOutlined />ZIMSEC-style grading from A to U by default, editable per school</li>
            <li><CheckCircleOutlined />One-page report cards with grades, positions and attendance</li>
            <li><CheckCircleOutlined />Publish a whole class at once, with any missing subjects flagged first</li>
            <li><CheckCircleOutlined />Only the principal or school admin can publish results</li>
          </ul>
          <p className="mkt-section-body">
            See how <RouterLink to="/features/zimsec-exams">ZIMSEC grading and exams</RouterLink> and{' '}
            <RouterLink to="/features/reports">report cards</RouterLink> work.
          </p>
        </div>
      </section>

      <section className="mkt-section mkt-section-alt">
        <div className="mkt-content">
          <h2 className="mkt-section-heading">What it costs</h2>
          <p className="mkt-section-body">
            The core platform is free for one full term, then $0.50 per student per month,
            with no setup fee. A secondary school with 600 pupils pays $300 a month.
            Add-ons such as accounting are optional and billed separately. Full details are
            on the <RouterLink to="/pricing">pricing page</RouterLink>.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
};
